import { useRef } from "react";
import { graphql, useFragment } from "react-relay";
import { useIntl, FormattedMessage } from "react-intl";
import * as v from "valibot";
import Suspense from "@/common/Suspense";
import { useValibotForm, useValibotSearchParams } from "@/utils/validation";
import { useOnChangeEffect, useOnceEffect } from "@/utils/hooks";
import { Form } from "@/kit/ui/form";
import { schemaFactory, Schema } from "./schema";
import { Filters } from "./Filters";
import Submissions, { SubmissionsHandle } from "./Submissions";
import { CompetitionApprovalsFragment$key } from "./__generated__/CompetitionApprovalsFragment.graphql";
import CompetitionScoringCriteria, {
  CompetitionScoringCriteriaHandle,
} from "../CompetitionScoringCriteria";

const Fragment = graphql`
  fragment CompetitionApprovalsFragment on Competition
  @argumentDefinitions(
    owner: { type: "String!" }
    approvalFilter: { type: "ApprovalFilter" }
    stage: { type: "Int" }
  ) {
    viewerCanApprove: viewerCan(action: CREATE_PROJECT_VERSION_APPROVAL)
    viewerCanReadPrivateApproval: viewerCan(
      action: READ_COMPETITION_PRIVATE_APPROVAL
    )
    hasSubmissions
    ...FiltersForCompetitionApprovalsFragment
    ...SubmissionsCompetitionApprovalsFragment
      @arguments(owner: $owner, approvalFilter: $approvalFilter, stage: $stage)
    ...CompetitionScoringCriteriaFragment
      @arguments(stage: $stage, owner: $owner)
  }
`;

export interface CompetitionApprovalsProps {
  defaultFilters: v.InferInput<Schema>;
  competition: CompetitionApprovalsFragment$key;
}

export default function CompetitionApprovals(props: CompetitionApprovalsProps) {
  const intl = useIntl();
  const competition = useFragment(Fragment, props.competition);
  const submissionsRef = useRef<SubmissionsHandle>(null);
  const criteraRef = useRef<CompetitionScoringCriteriaHandle>(null);
  const schema = schemaFactory(intl);
  const [_, setSearchParams] = useValibotSearchParams(schema);
  const filtersForm = useValibotForm(schema, {
    defaultValues: props.defaultFilters,
  });
  const filters = filtersForm.watch();

  useOnceEffect(() => {
    if (
      typeof props.defaultFilters.approvalFilter === "undefined" &&
      competition.viewerCanApprove
    ) {
      filtersForm.setValue("approvalFilter", "AWAITING_MINE");
    }
  });

  useOnChangeEffect(filters, () => {
    setSearchParams(filters);

    const parsed = v.safeParse(schema, filters);
    if (parsed.success) {
      submissionsRef.current?.refetch(parsed.output);
      criteraRef.current?.refetch({ stage: parsed.output.stage ?? null });
    }
  });

  return (
    <>
      <Suspense>
        <CompetitionScoringCriteria
          ref={criteraRef}
          competition={competition}
        />
      </Suspense>

      {competition.hasSubmissions && (
        <>
          <h1 className="text-2xl font-bold w-full font-poppins">
            <FormattedMessage defaultMessage="Submissions" />
          </h1>
          <Form form={filtersForm} className="flex flex-col gap-4">
            <Filters competition={competition} />
            <Suspense>
              <Submissions ref={submissionsRef} competition={competition} />
            </Suspense>
          </Form>
        </>
      )}
    </>
  );
}
